import clsx from 'clsx';
import { type ReactNode } from 'react';
import { Icon } from '../Core/Icon';
import FileIcon from './FileIcon';
import { formatFileSize } from '../../services/utils';
import { FileUploadStatusEnum } from '../../enums/FileUploadStatusEnum';
import type GroupedUploadItem from '../../interfaces/GroupedUploadItem';
import { useFileUpload } from '../../hooks/useFileUpload';
import TruncatedTooltip from './TruncatedTooltip';
import { Button, Spin } from 'antd';

interface Props {
  files: GroupedUploadItem[];
  handleRemoveFile: (file: GroupedUploadItem) => void;
}

const QuickShareFilesList = ({ files, handleRemoveFile }: Props) => {
  const { isUploadingQuickShareFiles } = useFileUpload();
  const { handleOpenUploadingFilesErrorsModal } = useFileUpload();
  return (
    <div
      className={clsx(
        'my-2 h-full max-h-52 w-full divide-y divide-gray overflow-auto border-b',
        files.length !== 0 ? 'border-gray' : 'border-transparent',
      )}
    >
      {files.map((file, index) => (
        <div
          key={index}
          className="flex h-10 w-full flex-row items-center justify-between gap-2 overflow-hidden px-2"
        >
          <div className="flex items-center">
            <div className="flex min-w-0 flex-row items-center gap-1">
              <FileIcon
                fileExtension={file.fileExtension || ''}
                isFolder={file.isFolder}
                className="h-4 w-4 flex-shrink-0"
              />
              <h2 className="max-w-64 font-semibold text-blue-dark">
                <TruncatedTooltip>{file.fileName}</TruncatedTooltip>
              </h2>
            </div>
          </div>

          <div className="flex flex-shrink-0 flex-row items-center justify-center gap-2">
            <div className="text-center align-baseline text-sm leading-none text-gray-dark">
              {formatFileSize(file.fileSize)}
            </div>

            {((): ReactNode => {
              switch (file.status) {
                case FileUploadStatusEnum.Pending:
                  return (
                    <>
                      {!isUploadingQuickShareFiles && (
                        <Button
                          type="text"
                          aria-label="Remove file"
                          disabled={isUploadingQuickShareFiles}
                          onClick={() => handleRemoveFile(file)}
                          className="p-0 text-sm text-gray-dark"
                        >
                          <Icon name="XIcon" className="h-5 w-5" />
                        </Button>
                      )}
                      {isUploadingQuickShareFiles && (
                        <>
                          <Icon
                            name={file.isFolder ? 'FolderUpIcon' : 'FileUpIcon'}
                            className="h-4 w-4 text-azure"
                          />
                          <Spin size="small" percent={file.progress} />
                          <span className="w-9 text-sm">{`${file.progress}%`}</span>
                        </>
                      )}
                    </>
                  );
                case FileUploadStatusEnum.Uploading:
                  return (
                    <>
                      <Icon
                        name={file.isFolder ? 'FolderUpIcon' : 'FileUpIcon'}
                        className="h-4 w-4 text-azure"
                      />
                      <Spin size="small" percent={file.progress} />
                      <span className="w-9 text-sm">{`${file.progress}%`}</span>
                    </>
                  );
                case FileUploadStatusEnum.Failed:
                  return (
                    <>
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          handleOpenUploadingFilesErrorsModal(true);
                        }}
                      >
                        <Icon name="CircleXIcon" className="h-4 w-4 text-red" />
                      </div>
                      <Spin size="small" percent={file.progress} />
                      <span className="w-9 text-sm text-blue-dark">{`${file.progress}%`}</span>
                    </>
                  );
                case FileUploadStatusEnum.FailedSharing:
                  return (
                    <>
                      <Icon name="TriangleAlertIcon" className="h-4 w-4 text-yellow-dark" />
                      <Spin size="small" percent={file.progress} />
                      <span className="w-9 text-sm text-blue-dark">{`${file.progress}%`}</span>
                    </>
                  );
                case FileUploadStatusEnum.Completed:
                  return (
                    <>
                      <Icon name="CircleCheckIcon" className="h-4 w-4 text-green" />
                      <Spin size="small" percent={file.progress} />
                      <span className="w-9 text-sm text-blue-dark">{`${file.progress}%`}</span>
                    </>
                  );
                default:
                  return 'N/D';
              }
            })()}
          </div>
        </div>
      ))}
    </div>
  );
};

export default QuickShareFilesList;
